import { cores } from '../styles/cores';

// ---------- BarraProgresso ----------
export function BarraProgresso({ percentual = 0, cor = cores.primario, altura = 8 }) {
  const valor = Math.max(0, Math.min(100, percentual));
  return (
    <div
      style={{
        width: '100%',
        height: altura,
        backgroundColor: cores.card,
        borderRadius: altura,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${valor}%`,
          height: '100%',
          backgroundColor: cor,
          borderRadius: altura,
          transition: 'width 0.3s ease',
        }}
      />
    </div>
  );
}

// ---------- BadgeStatus ----------
const ESTILOS_STATUS = {
  em_andamento: { texto: 'Em andamento', cor: cores.primario, fundo: '#EAE8FB' },
  aguardando: { texto: 'Aguardando', cor: cores.textoSecundario, fundo: '#EFEEEA' },
  pausado: { texto: 'Pausado', cor: cores.textoSecundario, fundo: '#EFEEEA' },
  atrasado: { texto: 'Atrasado', cor: cores.erro, fundo: '#FBE6E6' },
  atrasada: { texto: 'Atrasada', cor: cores.erro, fundo: '#FBE6E6' },
  concluida: { texto: 'Concluída', cor: cores.sucesso, fundo: '#E1F5EE' },
};

export function BadgeStatus({ status }) {
  const estilo = ESTILOS_STATUS[status] || ESTILOS_STATUS.aguardando;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '4px 10px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        color: estilo.cor,
        backgroundColor: estilo.fundo,
        whiteSpace: 'nowrap',
      }}
    >
      {status === 'concluida' && '✓ '}
      {status === 'em_andamento' && '▷ '}
      {(status === 'atrasado' || status === 'atrasada') && '⚠ '}
      {estilo.texto}
    </span>
  );
}

// ---------- BadgeCategoria ----------
const ESTILOS_CATEGORIA = {
  Desenvolvimento: { cor: cores.primario, fundo: '#EAE8FB' },
  Infra: { cor: cores.sucesso, fundo: '#E1F5EE' },
  Design: { cor: cores.alerta, fundo: '#FCEFDB' },
  Dados: { cor: '#3B7DD8', fundo: '#E3EDFB' },
  Outros: { cor: cores.textoSecundario, fundo: '#EFEEEA' },
};

export function BadgeCategoria({ categoria }) {
  const estilo = ESTILOS_CATEGORIA[categoria] || ESTILOS_CATEGORIA.Outros;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 10px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        color: estilo.cor,
        backgroundColor: estilo.fundo,
        whiteSpace: 'nowrap',
      }}
    >
      {categoria}
    </span>
  );
}

// ---------- Avatar ----------
const CORES_AVATAR = ['#534AB7', '#1D9E75', '#EF9F27', '#3B7DD8', '#E24B4A', '#9C5FCE'];

const obterIniciais = (nome = '') => {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
};

const corPorNome = (nome = '') => {
  let soma = 0;
  for (let i = 0; i < nome.length; i += 1) soma += nome.charCodeAt(i);
  return CORES_AVATAR[soma % CORES_AVATAR.length];
};

export function Avatar({ nome = '', size = 36 }) {
  const iniciais = nome.length <= 2 && nome === nome.toUpperCase() ? nome : obterIniciais(nome);
  return (
    <div
      title={nome}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: corPorNome(nome),
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.4,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {iniciais}
    </div>
  );
}

// ---------- Spinner ----------
export function Spinner({ tamanho = 32 }) {
  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 40,
        }}
      >
        <div
          style={{
            width: tamanho,
            height: tamanho,
            border: `3px solid ${cores.card}`,
            borderTopColor: cores.primario,
            borderRadius: '50%',
            animation: 'girar 0.8s linear infinite',
          }}
        />
      </div>
      <style>{`@keyframes girar { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}

// ---------- Btn ----------
const ESTILOS_BTN = {
  primario: {
    backgroundColor: cores.primario,
    color: '#fff',
    border: '1px solid transparent',
  },
  secundario: {
    backgroundColor: '#fff',
    color: cores.texto,
    border: `1px solid ${cores.card}`,
  },
  perigo: {
    backgroundColor: cores.erro,
    color: '#fff',
    border: '1px solid transparent',
  },
  fantasma: {
    backgroundColor: 'transparent',
    color: cores.primario,
    border: '1px solid transparent',
  },
};

export function Btn({ variante = 'primario', children, style, ...props }) {
  return (
    <button
      {...props}
      style={{
        ...ESTILOS_BTN[variante],
        padding: '10px 18px',
        borderRadius: 8,
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        transition: 'opacity 0.15s ease',
        ...style,
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.opacity = '0.85';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.opacity = '1';
      }}
    >
      {children}
    </button>
  );
}

// ---------- Campo ----------
export function Campo({ label, erro, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
      {label && (
        <label style={{ fontSize: 13, fontWeight: 600, color: cores.texto }}>{label}</label>
      )}
      {children}
      {erro && <span style={{ fontSize: 12, color: cores.erro }}>{erro}</span>}
    </div>
  );
}

const estiloCampo = {
  padding: '10px 12px',
  borderRadius: 8,
  border: `1px solid ${cores.card}`,
  fontSize: 14,
  color: cores.texto,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: '#fff',
  fontFamily: 'inherit',
};

// ---------- Input ----------
export function Input({ style, ...props }) {
  return (
    <input
      {...props}
      style={{ ...estiloCampo, ...style }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = cores.primario;
        props.onFocus?.(e);
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = cores.card;
        props.onBlur?.(e);
      }}
    />
  );
}

// ---------- Select ----------
export function Select({ style, children, ...props }) {
  return (
    <select
      {...props}
      style={{ ...estiloCampo, cursor: 'pointer', ...style }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = cores.primario;
        props.onFocus?.(e);
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = cores.card;
        props.onBlur?.(e);
      }}
    >
      {children}
    </select>
  );
}

// ---------- Modal ----------
export function Modal({ titulo, aberto, aoFechar, children, largura = 480 }) {
  if (!aberto) return null;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(44, 44, 42, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: 12,
          padding: 24,
          width: '100%',
          maxWidth: largura,
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 18, color: cores.texto }}>{titulo}</h2>
          <button
            onClick={aoFechar}
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: 22,
              cursor: 'pointer',
              color: cores.textoSecundario,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ---------- EstadoVazio ----------
export function EstadoVazio({ titulo, descricao, acao }) {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: '48px 24px',
        color: cores.textoSecundario,
      }}
    >
      <p style={{ fontSize: 16, fontWeight: 600, color: cores.texto, margin: '0 0 6px' }}>
        {titulo}
      </p>
      {descricao && <p style={{ fontSize: 14, margin: '0 0 16px' }}>{descricao}</p>}
      {acao}
    </div>
  );
}
